import React, { useState, useRef } from 'react';
import AdminDataTable from './AdminDataTable';
import AdminForm from './AdminForm';
import { requireAdminAccess } from '../common/SecureAdminPage';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import api from '../../services/api';
import * as XLSX from 'xlsx';

const AdminList = () => {
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { toCreate, toUpdate, totalRows, file }
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Export all admins to CSV
  const handleExportAll = async () => {
    try {
      const response = await api.get('/api/v1/admins/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admins_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      dispatch(addNotification({
        type: 'success',
        message: t('admin.export.success')
      }));
    } catch (error) {
      console.error('Export failed:', error);
      dispatch(addNotification({
        type: 'error',
        message: t('admin.export.error')
      }));
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/v1/admins/import-template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'admin_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      dispatch(addNotification({
        type: 'success',
        message: t('admin.template.downloaded')
      }));
    } catch (error) {
      console.error('Template download failed:', error);
      dispatch(addNotification({
        type: 'error',
        message: t('admin.template.error')
      }));
    }
  };

  // Handle file selection and import
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected if cancelled
    event.target.value = '';

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      dispatch(addNotification({
        type: 'error',
        message: t('admin.import.invalidFileType')
      }));
      return;
    }

    try {
      // Parse xlsx in browser to get usernames
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (rows.length < 2) {
        dispatch(addNotification({ type: 'error', message: t('admin.import.emptyFile') }));
        return;
      }
      const headers = rows[0].map(h => String(h).trim());
      const usernameIndex = headers.indexOf('username');
      const xlsxUsernames = rows.slice(1).map(row => {
        return String(row[usernameIndex] || '').trim();
      }).filter(u => u.length > 0);

      // Fetch existing admins to compare
      const existingRes = await api.get('/api/v1/admins/');
      const existingUsernames = new Set((existingRes.data || []).map(a => a.username));

      const toCreate = xlsxUsernames.filter(u => !existingUsernames.has(u)).length;
      const toUpdate = xlsxUsernames.filter(u => existingUsernames.has(u)).length;

      setImportPreview({ toCreate, toUpdate, totalRows: xlsxUsernames.length, file });
    } catch (error) {
      console.error('Preview failed:', error);
      dispatch(addNotification({ type: 'error', message: t('admin.import.error') }));
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importPreview.file);
    setImportPreview(null);
    try {
      const response = await api.post('/api/v1/admins/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      dispatch(addNotification({
        type: 'success',
        message: t('admin.import.success', {
          created: response.data.created,
          updated: response.data.updated,
          errors: response.data.errors
        })
      }));
      window.location.reload();
    } catch (error) {
      console.error('Import failed:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.detail || t('admin.import.error')
      }));
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setImportPreview(null);
  };

  return (
    <div className="space-y-6">
      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span className="text-blue-800 text-sm font-medium">
            🔒 {t('admin.secureNotice')}
          </span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.headerTitle')}</h1>
          <p className="text-gray-600">{t('admin.headerSubtitle')}</p>
        </div>
        <div className="flex gap-2">
          {/* Export All Button */}
          <button
            onClick={handleExportAll}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            title={t('admin.export.tooltip')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('admin.export.button')}
          </button>

          {/* Download Template Button */}
          <button
            onClick={handleDownloadTemplate}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            title={t('admin.template.tooltip')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            {t('admin.template.button')}
          </button>

          {/* Import Button */}
          <button
            onClick={handleImportClick}
            disabled={importing}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('admin.import.tooltip')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {importing ? t('admin.import.importing') : t('admin.import.button')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Add New Admin Button */}
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('admin.list.addNewAdmin')}
          </button>
        </div>
      </div>

      {/* Admin DataTable */}
      <AdminDataTable />

      {/* Create Admin Form Modal */}
      {showForm && (
        <AdminForm
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
          }}
        />
      )}

      {/* Import Confirmation Modal */}
      {importPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-yellow-100 p-2 rounded-full">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('admin.import.confirm.title')}</h3>
            </div>

            <p className="text-gray-600 text-sm mb-4">{t('admin.import.confirm.message')}</p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('admin.import.confirm.totalRows')}</span>
                <span className="font-semibold text-gray-900">{importPreview.totalRows}</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <span className="text-sm text-green-700 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('admin.import.confirm.toCreate')}
                </span>
                <span className="font-semibold text-green-700 text-lg">{importPreview.toCreate}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-700 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('admin.import.confirm.toUpdate')}
                </span>
                <span className="font-semibold text-blue-700 text-lg">{importPreview.toUpdate}</span>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelImport}
                className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                {t('admin.import.confirm.cancel')}
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={importing}
                className="px-5 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? t('admin.import.importing') : t('admin.import.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export with security wrapper
// TEMPORARILY DISABLED FOR DEBUGGING
export default AdminList;
// export default requireAdminAccess(AdminList);
